import type * as React from 'react';
import { get } from '@Domain/Api';
import grantedUsersFetched, {
	type GrantedUsersFetchedAction,
} from '@Application/SubjectTypeDefinitions/Actions/grantedUsersFetched';
import grantedUsersFetchFailed, {
	type GrantedUsersFetchFailedAction,
} from '@Application/SubjectTypeDefinitions/Actions/grantedUsersFetchFailed';
import type FetchGrantedUsers from '@Application/SubjectTypeDefinitions/Queries/FetchGrantedUsers';
import type { UsersListFetchedPayload } from '@Application/Users/Users/Actions/usersFetched';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../parameters';

type Actions = GrantedUsersFetchedAction | GrantedUsersFetchFailedAction;

/**
 * Resolves the names of the users a subject type is granted to. Writes to
 * `subjectTypeDefinitionsStore.grantedUsers`, never to `usersStore` (owned by the Users screen).
 */
export default (query: FetchGrantedUsers) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Without an `id` filter the endpoint would return every user: nothing to resolve, nothing to ask.
			const results =
				query.ids.length === 0
					? []
					: (
							await get<UsersListFetchedPayload>(
								`${parameters.api.host}/api/accounts/${query.accountId}/users?${query.toQueryString()}`,
							)
						).results;

			const action = grantedUsersFetched(
				results.map(({ id, firstName, lastName }) => ({ id, firstName, lastName })),
				query.accountId,
				query.ids,
			);
			dispatch(action);
			return action;
		} catch (error) {
			const action = grantedUsersFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
